// pages/LeaderPage.jsx — Anamoria SPA
// v1.11 — NEW-177: the dashboard dates label shows "From {start}" when a cohort has a start date but no
//   end date (was "Dates not set" unless BOTH were set, which hid an already-started pilot). Display-only. (Nov 2, 2026)
// v1.10 — NEW-171 (collapsible onboarding sections): the Welcome Email, Group QR, and Troubleshooting
//   guide cards start COLLAPSED (Group Prompts already did); the Pre-Launch Checklist is expanded until
//   all items are checked, then auto-collapses. All toggleable (in-memory, per visit). (Nov 2, 2026)
// v1.9 — NEW-171 Phase 3 (Leader Home, increment C): phased no-group-space empty state — phase 1
//   (no personal space) "Start with your own space" opens a private-space create and returns to the
//   dashboard; phase 2 (has a personal space) "There isn't a group space yet" creates the group space.
//   Hides the rollup stats, weekly chart, and week tabs while there is no group space (keeps the
//   Pre-Launch Checklist). Personal-space signal fixed from isPinned to an owned non-group space.
//   Draft copy. (Nov 2, 2026)
// v1.8 — NEW-171 Phase 3 (SPA PR-2 / 2c): (1) cohort-aware create — groupParam passed to
//   CreateSpaceModal so a group space is created for the SELECTED cohort (backend #162); (2)
//   no-group-space empty state (admin provisions the cohort; the leader creates the space) with a
//   "Create a group space" action that reloads the dashboard for that cohort on success; (3)
//   back-link falls back to the leader's pinned personal space when the cohort has no group space
//   (was: hidden). Draft copy. (Nov 2, 2026)
// v1.7 — NEW-171 Phase 3 (SPA PR-2): leader group switcher (title-as-control, D-S4) + wayfinding.
//   GET /leader/groups only when dash.multiCohortEnabled; threads ?group=<selectedGroupId> on EVERY
//   cohort-scoped leader call (dashboard, week, checkin GET/POST, checklist PATCH) AND into the prompts
//   + email-list sub-components; resets week caches on switch; back-link uses dash.groupSpaceId/Name.
//   Gate/partition in utils/cohortSwitcher. Dark: switcher hidden unless flag on AND 2+ groups. Draft copy. (Nov 1, 2026)
// v1.6 — Leader dashboard enhancements (2026-10-18): (1) invite picker always offers a
//   "+ New group space" create row (empty + populated) that opens the create modal with Group preselected;
//   (2) "Add email list" restyled as a visible button (.module.css .checkAction);
//   (3) Pre-Launch Checklist reordered + reworded to 6 items; existing ids (tested/participants/
//   welcome/kickoff) KEPT so completion state is preserved, plus new ids prompts/createspace
//   (backend anamoria-leader v1.10 allowlists them); (4) dashboard back link returns to the
//   governed group space, labeled "{name} group space" (retires "My Memory Vault"). Frontend-only.
// v1.5 — v0.3: invite space-picker lists GROUP spaces only (isGroupSpace); empty state added.
// v1.4 — B3: flag-gated "Invite people" header entry + space-picker modal
//   (dark unless group_spaces_enabled; routes to /spaces/:id/invite/bulk).
//   Additive; no change to existing dashboard behavior. (July 26, 2026)
// v1.3 — May 25, 2026
// Route: /leader (protected — JWT required, leader role enforced by API)
//
// Changes from v1.2:
//   - F-3: Moved Group Prompts section above Troubleshooting FAQ
//
// Consumes:
//   GET   /leader/dashboard    → group context + metrics + participants + weeklyStats + leaderChecklist
//   GET   /leader/week/{num}   → per-week breakdown + participants
//   GET   /leader/checkin/{num}→ submitted check-in or 404
//   POST  /leader/checkin      → submit weekly check-in (immutable)
//   PATCH /leader/checklist    → toggle checklist item (server-persisted)
//
// Changes from v1.0:
//   #1  formatDate() — fix UTC→local off-by-one (parse date parts, avoid timezone shift)
//   #2  Stats labels — "Memories" → "Total Memories", "Voice" → "Voice Memories"
//   #3  Checklist — removed "All participant accounts created"; localStorage → server persistence
//   #4  Email template — replaced with approved copy; group code auto-inserted
//   #5  QR card text — updated to "Share this QR code during group sessions..."
//   #7  FAQ text — "Auth0" → "Anamoria"; case-sensitivity wording corrected; password ref removed
//   #8  Participant counter — "memories" → "total memories"
//   #10 Check-in confirm — window.confirm → styled modal
//
// QR code: client-side via qrcode.react (Option A)
// Checklist: server-persisted via PATCH /leader/checklist (pilot_groups.metadata)

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { createApiClient } from '../api/client';
import { useAppContext } from '../App';
import { QRCodeSVG } from 'qrcode.react';
import s from './LeaderPage.module.css';
import LeaderPromptsSection from './LeaderPromptsSection';
import EmailListUploadModal from '../components/EmailListUploadModal';
import CreateSpaceModal from '../components/CreateSpaceModal';
import { shouldShowSwitcher, partitionCohorts } from '../utils/cohortSwitcher';

// ─── Static Data ─────────────────────────────────────────────────────────

const THEMES = { 1:'Their Story', 2:'What I Miss', 3:'Rituals', 4:'Legacy' };
const PROMPTS = {
  1:'One thing I never want to forget...',
  2:'What I miss most is...',
  3:'A small ritual that makes me feel close to them is...',
  4:'What I want others to remember about them is...',
};

const FAQ = [
  { q:"A participant can't log in", a:"Make sure they're using the email address you have on file. They should check their spam folder for the Anamoria verification email. If the issue persists, contact support@anamoria.com." },
  { q:"The group code isn't working", a:"Group codes are not case-sensitive, so capitalization does not matter. Confirm the code on your dashboard matches what the participant is entering. If the group hasn't been activated yet, the code won't be accepted." },
  { q:"A participant didn't receive the welcome email", a:"Check that their email is spelled correctly. Ask them to check spam/junk folders. You can resend the welcome instructions manually using the email template on this page." },
  { q:"Someone wants to join mid-pilot", a:"They can join at any time during the active pilot period using the group code. They'll start with the current week's prompt but can record memories about any topic." },
  { q:"How do participants record a voice note?", a:"After logging in and creating a space, they tap the microphone button. The app requests microphone permission on first use. Voice notes can be up to 5 minutes. They can re-record before saving." },
];

const CHECKLIST = [
  { id:'tested', label:'Start your own space and try it for yourself, before you invite your group' },
  { id:'prompts', label:'Review standard prompts and add custom ones if needed' },
  { id:'createspace', label:'Create a group space' },
  { id:'participants', label:'Add participant email list' },
  { id:'welcome', label:'Invite participants or send welcome emails, if needed' },
  { id:'kickoff', label:'Schedule group kickoff meeting' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────

function heightTier(pct, isFuture) {
  if (isFuture) return s.barH0;
  if (pct <= 0) return s.barH0;
  if (pct <= 10) return s.barH10;
  if (pct <= 20) return s.barH20;
  if (pct <= 30) return s.barH30;
  if (pct <= 40) return s.barH40;
  if (pct <= 50) return s.barH50;
  if (pct <= 60) return s.barH60;
  if (pct <= 70) return s.barH70;
  if (pct <= 80) return s.barH80;
  if (pct <= 90) return s.barH90;
  return s.barH100;
}

function formatDate(d) {
  if (!d) return null;
  // Handle UTC date strings — prevent timezone shift showing previous day
  const str = String(d);
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]))
      .toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
  }
  return new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
}

function initial(name) {
  return (name || '?').charAt(0).toUpperCase();
}

// ─── Component ───────────────────────────────────────────────────────────

export default function LeaderPage() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const appState = useAppContext();
  // B3: gate the bulk-invite entry (dark unless group_spaces_enabled).
  const groupSpacesEnabled = appState?.flags?.group_spaces_enabled === true;

  const getApi = useCallback(
    () => createApiClient(getAccessTokenSilently),
    [getAccessTokenSilently]
  );

  // ── Dashboard state ──
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ── Week selection ──
  const [selectedWeek, setSelectedWeek] = useState(null); // set after dash loads
  const [subTab, setSubTab] = useState('activity'); // 'activity' | 'checkin'

  // ── Weekly data caches ──
  const [weekCache, setWeekCache] = useState({}); // weekNum → { participants, breakdown }
  const [checkinCache, setCheckinCache] = useState({}); // weekNum → checkin obj or 'none'
  const [weekLoading, setWeekLoading] = useState(false);

  // ── Check-in form ──
  const [engLevel, setEngLevel] = useState(null);
  const [concerns, setConcerns] = useState('');
  const [verbatim, setVerbatim] = useState('');
  const [positiveFeedback, setPositiveFeedback] = useState('');
  const [techIssues, setTechIssues] = useState(null); // true/false
  const [techDetails, setTechDetails] = useState('');
  const [followUp, setFollowUp] = useState(null); // true/false
  const [followUpNotes, setFollowUpNotes] = useState('');
  const [priorFollowup, setPriorFollowup] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // ── UI ──
  const [toast, setToast] = useState('');
  const [showQrModal, setShowQrModal] = useState(false);
  const [showInvitePicker, setShowInvitePicker] = useState(false); // B3
  const [showEmailUpload, setShowEmailUpload] = useState(false); // GS-11 bulk allowlist upload
  const [showCreateSpace, setShowCreateSpace] = useState(false); // v1.6: create a group space from the invite picker
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  // NEW-171 collapsible onboarding cards: guide cards start collapsed; checklist expanded until all checked.
  const [checklistManual, setChecklistManual] = useState(null); // null = auto (follow completion)
  const [tplOpen, setTplOpen] = useState(false);
  const [qrCardOpen, setQrCardOpen] = useState(false);
  const [troubleshootOpen, setTroubleshootOpen] = useState(false);

  // ── Checklist (server-persisted via pilot_groups.metadata) ──
  const [checklist, setChecklist] = useState({});

  // ── NEW-171 Phase 3 (PR-2): group switcher ──
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null); // null = home cohort (D-S1: local state)
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const groupParam = selectedGroupId ? `?group=${selectedGroupId}` : '';
  const [createSpaceIntent, setCreateSpaceIntent] = useState('invite'); // 'invite' (B3 picker) | 'cohort' (2c group space) | 'personal' (phase-1 own space)
  const [dashReloadKey, setDashReloadKey] = useState(0); // bump to re-fetch the dashboard after creating this cohort's group space

  // ── Load dashboard ──
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      setWeekCache({});
      setCheckinCache({});
      try {
        const api = getApi();
        const data = await api.get(`/leader/dashboard${groupParam}`);
        if (cancelled) return;
        setDash(data);
        setChecklist(data.leaderChecklist || {});
        setSelectedWeek(data.currentWeek > 0 ? data.currentWeek : 0);
      } catch (err) {
        if (cancelled) return;
        if (err.error === 'LEADER_ROLE_REQUIRED' || err.error === 'NOT_A_LEADER') {
          setError('ACCESS_DENIED');
        } else {
          setError(err.error || 'LOAD_FAILED');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [getApi, groupParam, dashReloadKey]);

  // ── Load the leader's groups for the switcher — only when multi-cohort is enabled ──
  useEffect(() => {
    if (!dash?.multiCohortEnabled) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getApi().get('/leader/groups');
        if (!cancelled) setGroups(Array.isArray(data?.groups) ? data.groups : []);
      } catch { /* switcher stays hidden on failure */ }
    })();
    return () => { cancelled = true; };
  }, [dash?.multiCohortEnabled, getApi]);

  // ── Load week data when selectedWeek changes ──
  useEffect(() => {
    if (!dash || selectedWeek === null || selectedWeek === 0) return;
    if (selectedWeek > dash.currentWeek) return; // future week
    if (weekCache[selectedWeek]) return; // already cached

    let cancelled = false;
    async function loadWeek() {
      setWeekLoading(true);
      try {
        const api = getApi();
        const [weekData, checkinData] = await Promise.all([
          api.get(`/leader/week/${selectedWeek}${groupParam}`),
          api.get(`/leader/checkin/${selectedWeek}${groupParam}`).catch(e =>
            e.error === 'NOT_FOUND' ? null : Promise.reject(e)
          ),
        ]);
        if (cancelled) return;
        setWeekCache(prev => ({ ...prev, [selectedWeek]: weekData }));
        setCheckinCache(prev => ({ ...prev, [selectedWeek]: checkinData || 'none' }));
      } catch (err) {
        console.error('Week load error:', err);
      } finally {
        if (!cancelled) setWeekLoading(false);
      }
    }
    loadWeek();
    return () => { cancelled = true; };
  }, [dash, selectedWeek, weekCache, getApi, groupParam]);

  // ── Derived data ──
  const totalWeeks = dash?.totalWeeks || 4;
  const currentWeek = dash?.currentWeek || 0;
  const weekData = selectedWeek ? weekCache[selectedWeek] : null;
  const checkinData = selectedWeek ? checkinCache[selectedWeek] : null;
  const isSubmitted = checkinData && checkinData !== 'none';
  const isFutureWeek = selectedWeek > currentWeek;
  const checkDone = CHECKLIST.filter(c => checklist[c.id]).length;
  const allChecked = CHECKLIST.length > 0 && checkDone >= CHECKLIST.length;
  const checklistOpen = checklistManual !== null ? checklistManual : !allChecked;

  const totalMemories = useMemo(() => {
    if (!dash?.memoriesByType) return 0;
    return Object.values(dash.memoriesByType).reduce((sum, n) => sum + n, 0);
  }, [dash]);

  const voiceCount = dash?.memoriesByType?.voice || 0;

  // ── Max values for bar chart scaling ──
  const maxBar = useMemo(() => {
    if (!dash?.weeklyStats) return 1;
    let m = 1;
    for (const ws of dash.weeklyStats) {
      m = Math.max(m, ws.memories, ws.voiceNotes);
    }
    return m;
  }, [dash]);

  // ── Handlers ──

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  }

  async function copyText(text, label) {
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${label} copied!`);
    } catch {
      showToast('Copy failed — please copy manually');
    }
  }

  async function toggleCheck(id) {
    const newVal = !checklist[id];
    setChecklist(prev => ({ ...prev, [id]: newVal })); // optimistic update
    try {
      const api = getApi();
      await api.patch(`/leader/checklist${groupParam}`, { itemId: id, checked: newVal });
    } catch {
      setChecklist(prev => ({ ...prev, [id]: !newVal })); // revert on failure
      showToast('Failed to save — please try again');
    }
  }

  function selectWeek(wk) {
    if (wk > currentWeek && wk !== 0) return;
    setSelectedWeek(wk);
    setSubTab('activity');
    // Reset form when switching weeks
    resetForm();
  }

  function resetForm() {
    setEngLevel(null);
    setConcerns('');
    setVerbatim('');
    setPositiveFeedback('');
    setTechIssues(null);
    setTechDetails('');
    setFollowUp(null);
    setFollowUpNotes('');
    setPriorFollowup('');
  }

  async function handleSubmitCheckin() {
    if (!engLevel) { showToast('Please select an engagement level'); return; }
    setShowConfirmModal(true);
  }

  async function confirmSubmitCheckin() {
    setShowConfirmModal(false);
    setSubmitting(true);
    try {
      const api = getApi();
      const result = await api.post(`/leader/checkin${groupParam}`, {
        weekNumber: selectedWeek,
        engagementLevel: engLevel,
        leaderVerbatim: verbatim || null,
        concernsRaised: concerns || null,
        positiveFeedback: positiveFeedback || null,
        technicalIssues: techIssues === true,
        technicalIssueDetails: techDetails || null,
        followUpNeeded: followUp === true,
        followUpNotes: followUpNotes || null,
        priorWeekFollowup: priorFollowup || null,
      });
      // Refresh checkin cache with submitted data
      const fresh = await api.get(`/leader/checkin/${selectedWeek}${groupParam}`).catch(() => null);
      setCheckinCache(prev => ({ ...prev, [selectedWeek]: fresh || result }));
      showToast('Check-in submitted successfully');
      setSubTab('checkin');
    } catch (err) {
      if (err.error === 'ALREADY_SUBMITTED') {
        showToast('This check-in was already submitted');
        // Refresh cache
        const api = getApi();
        const fresh = await api.get(`/leader/checkin/${selectedWeek}${groupParam}`).catch(() => null);
        if (fresh) setCheckinCache(prev => ({ ...prev, [selectedWeek]: fresh }));
      } else {
        showToast('Submit failed — please try again');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ── Email template ──
  const emailTemplate = useMemo(() => {
    if (!dash) return '';
    const code = dash.accessCode || '[GROUP CODE]';
    return `Subject: A private place to keep your memories

Hi [FIRST_NAME],

I wanted to let you know about something our group will have access to over the coming weeks. It's a private space online where you can keep voice recordings, photos, and notes about your loved one.

It belongs to you, and no one else can see what you've added unless you decide to share it.

To get in:
1. Go to app.anamoria.org
2. Enter this Group Code: ${code} in the Just Joining section
3. Use the email address I have for you, and check your inbox for sign in code

If you have any trouble getting in, or anything at all you want to ask, just reply to this email.

Warmly,
[YOUR NAME]`;
  }, [dash]);

  const joinUrl = dash ? `https://app.anamoria.org/join?gc=${encodeURIComponent(dash.accessCode || '')}` : '';

  // ─── RENDER ────────────────────────────────────────────────────────────

  // Loading
  if (loading) {
    return (
      <div className={s.page}>
        <div className={s.loading}>
          <div className="app-loading-spinner" />
        </div>
      </div>
    );
  }

  // Access denied
  if (error === 'ACCESS_DENIED') {
    return (
      <div className={s.page}>
        <div className={s.errorBox}>
          <h2>Access Restricted</h2>
          <p>This page is only available to pilot group leaders.</p>
          <button className={s.retryBtn} onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  // Other errors
  if (error || !dash) {
    return (
      <div className={s.page}>
        <div className={s.errorBox}>
          <h2>Something went wrong</h2>
          <p>We couldn't load the dashboard. Please try again.</p>
          <button className={s.retryBtn} onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  const startStr = formatDate(dash.pilotStartDate);
  const endStr = formatDate(dash.pilotEndDate);
  // v1.6: back link returns to the group space this leader governs (the single group
  // space among their spaces), not the pinned personal space.
  // NEW-171 Phase 3 (PR-2): wayfinding uses the dashboard's active group space for the SELECTED cohort
  // (retires the appState.spaces.find(isGroupSpace) single-group guess; absorbs NEW-160c).
  const personalSpace = (appState?.spaces || []).find(sp => sp.isGroupSpace !== true) || null; // C: owned non-group (personal) space; NOT isPinned (new spaces aren't auto-pinned)
  const personalSpaceId = personalSpace?.id || null;
  const backSpaceId = dash?.groupSpaceId || null;
  const backSpaceName = dash?.groupSpaceName || null;
  const datesStr = startStr && endStr ? `${startStr} – ${endStr}` : startStr ? `From ${startStr}` : 'Dates not set';

  return (
    <div className={s.page}>
      <div className={s.dashboard}>

        {/* ── Back Link — only when the selected cohort has a group space (PR-2 fix) ── */}
        {backSpaceId ? (
          <button className={s.backLink} onClick={() => navigate(`/spaces/${backSpaceId}`)}>
            ← {backSpaceName ? `${backSpaceName} group space` : 'Back to group space'}
          </button>
        ) : personalSpaceId ? (
          <button className={s.backLink} onClick={() => navigate(`/spaces/${personalSpaceId}`)}>
            ← Back to your space
          </button>
        ) : null}

        {/* ══════════════════════════════════════════
            HEADER
            ══════════════════════════════════════════ */}
        <div className={s.header}>
          <div className={s.headerLeft}>
            {shouldShowSwitcher(dash.multiCohortEnabled, groups) ? (
            <div className={s.switcherWrap}>
              <button type="button" className={s.switcher} onClick={() => setSwitcherOpen(o => !o)} aria-haspopup="listbox" aria-expanded={switcherOpen}>
                <span className={s.switcherName}>{dash.groupName || 'Pilot Group'}</span>
                <svg className={s.switcherCaret} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
              </button>
              {switcherOpen && (() => {
                const { active, past } = partitionCohorts(groups);
                const currentId = selectedGroupId ?? dash.groupId;
                const pick = (id) => { setSwitcherOpen(false); if (id !== currentId) setSelectedGroupId(id); };
                return (
                  <div className={s.switcherMenu} role="listbox">
                    {active.map(gr => (
                      <button key={gr.groupId} type="button" role="option" aria-selected={gr.groupId === currentId} className={s.switcherItem} onClick={() => pick(gr.groupId)}>{gr.name}</button>
                    ))}
                    {past.length > 0 && <div className={s.switcherDivider}>Past</div>}
                    {past.map(gr => (
                      <button key={gr.groupId} type="button" role="option" aria-selected={gr.groupId === currentId} className={`${s.switcherItem} ${s.switcherItemPast}`} onClick={() => pick(gr.groupId)}>{gr.name}</button>
                    ))}
                  </div>
                );
              })()}
            </div>
          ) : (
            <h1 className={s.headerTitle}>{dash.groupName || 'Pilot Group'}</h1>
          )}
            <div className={s.headerMeta}>
              <div className={s.codeRow}>
                <span className={s.codeLabel}>Group Code:</span>
                <span className={s.codeValue}>{dash.accessCode}</span>
                <button className={s.btnSmall} onClick={() => copyText(dash.accessCode, 'Code')}>Copy</button>
                <button className={s.btnShowQr} onClick={() => setShowQrModal(true)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="3" height="3"/><rect x="20" y="14" width="1" height="3"/><rect x="14" y="20" width="3" height="1"/></svg>
                  Show QR
                </button>
                {/* B3: flag-gated bulk-invite entry (dark unless group_spaces_enabled). */}
                {groupSpacesEnabled && (
                  <button className={s.btnShowQr} onClick={() => setShowInvitePicker(true)}>
                    Invite people
                  </button>
                )}
              </div>
              <span className={s.headerDates}>{datesStr}</span>
            </div>
          </div>

          {backSpaceId && (
          <div className={s.headerStats}>
            <div className={s.stat}>
              <span className={`${s.statNumber} ${s.statMemories}`}>{totalMemories}</span>
              <span className={s.statLabel}>Total Memories</span>
            </div>
            <div className={s.stat}>
              <span className={`${s.statNumber} ${s.statVoice}`}>{voiceCount}</span>
              <span className={s.statLabel}>Voice Notes</span>
            </div>
            <div className={s.stat}>
              <span className={`${s.statNumber} ${s.statActive}`}>{dash.activatedUsers}/{dash.totalUsers}</span>
              <span className={s.statLabel}>Active</span>
            </div>
          </div>
          )}
        </div>

        {/* NEW-171 Phase 3 (Leader Home, increment C): phased empty state when the cohort has no group
            space (admin provisions the cohort; the leader creates the space). Phase 1 (no personal space)
            steers them to try their own space first (checklist item 1); phase 2 (has a personal space)
            offers the group space. Draft copy (brand-voice owed). */}
        {!backSpaceId && !personalSpaceId && (
          <div className={s.noSpaceCard}>
            <h2 className={s.noSpaceTitle}>Start with your own space</h2>
            <p className={s.noSpaceBody}>Try Anamoria yourself by creating a private space, before you set up your group.</p>
            <button
              className={s.noSpaceBtn}
              onClick={() => { setCreateSpaceIntent('personal'); setShowCreateSpace(true); }}
            >
              Create my space
            </button>
          </div>
        )}
        {!backSpaceId && personalSpaceId && (
          <div className={s.noSpaceCard}>
            <h2 className={s.noSpaceTitle}>There isn&apos;t a group space yet</h2>
            <p className={s.noSpaceBody}>Create a group space so your members have somewhere to gather and contribute.</p>
            <button
              className={s.noSpaceBtn}
              onClick={() => { setCreateSpaceIntent('cohort'); setShowCreateSpace(true); }}
            >
              Create a group space
            </button>
          </div>
        )}

        {/* ══════════════════════════════════════════
            BAR CHART
            ══════════════════════════════════════════ */}
        {backSpaceId && dash.weeklyStats && dash.weeklyStats.length > 0 && (
          <div className={s.insightsCard}>
            <div className={s.chartLabel}>Weekly Activity</div>
            <div className={s.chartBars}>
              {dash.weeklyStats.map(ws => {
                const memPct = maxBar > 0 ? (ws.memories / maxBar) * 100 : 0;
                const voicePct = maxBar > 0 ? (ws.voiceNotes / maxBar) * 100 : 0;
                return (
                  <div key={ws.week} className={s.chartWeek}>
                    <div className={s.chartBarGroup}>
                      <div className={`${s.chartBar} ${s.barMemories} ${heightTier(memPct, ws.isFuture)}`} />
                      <div className={`${s.chartBar} ${s.barVoice} ${heightTier(voicePct, ws.isFuture)}`} />
                    </div>
                    <div className={s.circleWrap}>
                      {ws.isFuture ? (
                        <span style={{ fontSize:11, color:'var(--text-muted)' }}>—</span>
                      ) : (
                        <span className={s.activeCircle}>{ws.activeParticipants}</span>
                      )}
                    </div>
                    <span className={s.weekLabel}>Wk {ws.week}</span>
                  </div>
                );
              })}
            </div>
            <div className={s.chartLegend}>
              <span className={s.legendItem}><span className={`${s.legendDot} ${s.dotMemories}`} /> Total Memories</span>
              <span className={s.legendItem}><span className={`${s.legendDot} ${s.dotVoice}`} /> Voice Notes</span>
              <span className={s.legendItem}><span className={`${s.legendDot} ${s.dotActive}`} /> Active</span>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════
            WEEK TABS
            ══════════════════════════════════════════ */}
        {backSpaceId && (
        <div className={s.weekNav}>
          {/* Week 0: Getting Started */}
          <button
            className={`${s.weekTab} ${s.weekTabOnboarding} ${selectedWeek === 0 ? s.weekTabSelected : ''}`}
            onClick={() => selectWeek(0)}
          >
            <span className={s.tabNumber}>Getting Started</span>
          </button>

          {/* Weeks 1..N */}
          {Array.from({ length: totalWeeks }, (_, i) => i + 1).map(wk => {
            const isSel = selectedWeek === wk;
            const isCur = wk === currentWeek;
            const isFut = wk > currentWeek;
            const isSub = checkinCache[wk] && checkinCache[wk] !== 'none';
            return (
              <button
                key={wk}
                className={[
                  s.weekTab,
                  isSel && s.weekTabSelected,
                  isSub && s.weekTabSubmitted,
                  isFut && s.weekTabFuture,
                ].filter(Boolean).join(' ')}
                onClick={() => selectWeek(wk)}
                disabled={isFut}
              >
                {isSub && <span className={s.tabCheck}>✓</span>}
                {isCur && <span className={s.tabCurrentDot} />}
                <span className={s.tabNumber}>Week {wk}</span>
                <span className={s.tabTheme}>{THEMES[wk] || ''}</span>
              </button>
            );
          })}
        </div>
        )}

        {/* ══════════════════════════════════════════
            WEEK 0 — GETTING STARTED
            ══════════════════════════════════════════ */}
        {(!backSpaceId || selectedWeek === 0) && (
          <div className={s.onboardingSection}>

            {/* Pre-Launch Checklist — expanded until all items checked, then auto-collapses (collapsible sections) */}
            <div className={s.card}>
              <button type="button" className={s.collapseHeader} onClick={() => setChecklistManual(!checklistOpen)} aria-expanded={checklistOpen}>
                <h3 className={s.cardTitle} style={{ margin: 0 }}>Pre-Launch Checklist</h3>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                  <span className={s.checkProgress}>{checkDone} of {CHECKLIST.length} complete</span>
                  <span className={`${s.collapseArrow} ${checklistOpen ? s.collapseArrowOpen : ''}`}>▾</span>
                </span>
              </button>
              {checklistOpen && (
              <div className={s.checkItems}>
                {CHECKLIST.map(item => (
                  <div key={item.id} className={s.checkRow}>
                    <button className={s.checkItem} onClick={() => toggleCheck(item.id)}>
                      <span className={`${s.checkBox} ${checklist[item.id] ? s.checkBoxDone : ''}`}>
                        {checklist[item.id] ? '✓' : ''}
                      </span>
                      <span className={checklist[item.id] ? s.checkLabelDone : ''}>{item.label}</span>
                    </button>
                    {item.id === 'participants' && groupSpacesEnabled && (
                      <button className={s.checkAction} onClick={() => setShowEmailUpload(true)}>Add email list</button>
                    )}
                  </div>
                ))}
              </div>
              )}
            </div>

            {/* Email Template */}
            <div className={s.card}>
              <button type="button" className={s.collapseHeader} onClick={() => setTplOpen(o => !o)} aria-expanded={tplOpen}>
                <h3 className={s.cardTitle} style={{ margin: 0 }}>Welcome Email Template</h3>
                <span className={`${s.collapseArrow} ${tplOpen ? s.collapseArrowOpen : ''}`}>▾</span>
              </button>
              {tplOpen && (
              <div className={s.collapseBody}>
                <p className={s.cardHint}>Copy and personalize this for your group. Replace bracketed text with your details.</p>
                <div className={s.emailTpl}>{emailTemplate}</div>
                <button className={s.btnCopyTpl} onClick={() => copyText(emailTemplate, 'Template')}>
                  Copy Template
                </button>
              </div>
              )}
            </div>

            {/* QR Code */}
            <div className={s.card}>
              <button type="button" className={s.collapseHeader} onClick={() => setQrCardOpen(o => !o)} aria-expanded={qrCardOpen}>
                <h3 className={s.cardTitle} style={{ margin: 0 }}>Group QR Code</h3>
                <span className={`${s.collapseArrow} ${qrCardOpen ? s.collapseArrowOpen : ''}`}>▾</span>
              </button>
              {qrCardOpen && (
              <div className={s.collapseBody}>
                <p className={s.cardHint}>Share this QR code during group sessions for easy access to Anamoria</p>
                <div className={s.qrWrap}>
                  <QRCodeSVG value={joinUrl} size={180} level="M" />
                </div>
              </div>
              )}
            </div>

            {/* Group Prompts — Track 2 */}
            <LeaderPromptsSection getApi={getApi} groupParam={groupParam} cardClass={s.card} titleClass={s.cardTitle} />

            {/* FAQ */}
            <div className={s.card}>
              <button type="button" className={s.collapseHeader} onClick={() => setTroubleshootOpen(o => !o)} aria-expanded={troubleshootOpen}>
                <h3 className={s.cardTitle} style={{ margin: 0 }}>Troubleshooting</h3>
                <span className={`${s.collapseArrow} ${troubleshootOpen ? s.collapseArrowOpen : ''}`}>▾</span>
              </button>
              {troubleshootOpen && (
              <div className={s.collapseBody}>
                <div className={s.faqList}>
                  {FAQ.map((item, i) => (
                    <div key={i} className={s.faqItem}>
                      <button className={s.faqQ} onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                        {item.q}
                        <span className={`${s.faqArrow} ${openFaq === i ? s.faqArrowOpen : ''}`}>▾</span>
                      </button>
                      {openFaq === i && <div className={s.faqA}>{item.a}</div>}
                    </div>
                  ))}
                </div>
              </div>
              )}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════
            WEEK 1-N — CONTENT
            ══════════════════════════════════════════ */}
        {backSpaceId && selectedWeek > 0 && !isFutureWeek && (
          <>
            {/* Week Banner */}
            <div className={s.weekBanner}>
              <div className={s.bannerTheme}>WEEK {selectedWeek}</div>
            </div>

            {/* Sub-tabs */}
            <div className={s.subTabBar}>
              <button className={`${s.subTab} ${subTab === 'activity' ? s.subTabActive : ''}`} onClick={() => setSubTab('activity')}>
                Participant Activity
              </button>
              <button className={`${s.subTab} ${subTab === 'checkin' ? s.subTabActive : ''}`} onClick={() => setSubTab('checkin')}>
                Weekly Check-In {isSubmitted && '✓'}
              </button>
            </div>

            <div className={s.subTabContent}>
              {weekLoading ? (
                <div className={s.weekLoading}>
                  <div className="app-loading-spinner" style={{ width:18, height:18, borderWidth:2 }} />
                  Loading week data...
                </div>
              ) : subTab === 'activity' ? (
                /* ── Participant Activity ── */
                <div>
                  {weekData && (
                    <div className={s.activitySummary}>
                      <span className={s.summaryText}>
                        {weekData.participants.filter(p => p.memoriesThisWeek > 0).length} of {weekData.participants.length} active this week
                      </span>
                    </div>
                  )}
                  {weekData?.participants.map(p => {
                    const hasActivity = p.memoriesThisWeek > 0;
                    return (
                      <div key={p.id} className={`${s.pRow} ${!hasActivity ? s.pRowPending : ''}`}>
                        <span className={`${s.avatar} ${hasActivity ? s.avatarActive : s.avatarPending}`}>
                          {initial(p.name)}
                        </span>
                        <div className={s.pInfo}>
                          <span className={s.pName}>{p.name}</span>
                          <span className={s.pMeta}>
                            {hasActivity
                              ? `${p.voiceNotesThisWeek} voice · ${p.memoriesThisWeek} total memories`
                              : 'Not yet activated'}
                          </span>
                        </div>
                        <div className={s.pActivity}>
                          {hasActivity ? (
                            <span className={s.badge}>{p.memoriesThisWeek} this week</span>
                          ) : (
                            <span className={s.statusPending}>Not activated</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {!weekData?.participants?.length && !weekLoading && (
                    <div style={{ padding:24, textAlign:'center', color:'var(--text-muted)' }}>
                      No participant data available for this week.
                    </div>
                  )}
                </div>
              ) : (
                /* ── Check-In Tab ── */
                <div className={s.checkinContent}>
                  {isSubmitted ? (
                    /* Submitted — read-only view */
                    <div>
                      <div className={s.submittedBadge}>
                        <span style={{ fontWeight:700 }}>✓</span>
                        Submitted on {formatDate(checkinData.reportDate)}
                      </div>

                      <div className={s.reportGrid}>
                        <div className={s.reportItem}>
                          <span className={s.reportLabel}>Engagement</span>
                          <span className={`${s.reportValue} ${
                            checkinData.engagementLevel === 'high' ? s.engHigh :
                            checkinData.engagementLevel === 'medium' ? s.engMedium : s.engLow
                          }`}>
                            {checkinData.engagementLevel ? checkinData.engagementLevel.charAt(0).toUpperCase() + checkinData.engagementLevel.slice(1) : '—'}
                          </span>
                        </div>
                      </div>

                      {checkinData.concernsRaised && (
                        <div className={s.reportBlock}>
                          <span className={s.reportBlockLabel}>Issues & Concerns</span>
                          <p className={s.reportBlockValue}>{checkinData.concernsRaised}</p>
                        </div>
                      )}
                      {checkinData.leaderVerbatim && (
                        <div className={s.reportBlock}>
                          <span className={s.reportBlockLabel}>Verbatim</span>
                          <p className={`${s.reportBlockValue} ${s.reportVerbatim}`}>"{checkinData.leaderVerbatim}"</p>
                        </div>
                      )}
                      {checkinData.positiveFeedback && (
                        <div className={s.reportBlock}>
                          <span className={s.reportBlockLabel}>Positive Feedback</span>
                          <p className={s.reportBlockValue}>{checkinData.positiveFeedback}</p>
                        </div>
                      )}
                      {checkinData.technicalIssues && checkinData.technicalIssueDetails && (
                        <div className={s.reportBlock}>
                          <span className={s.reportBlockLabel}>Technical Issues</span>
                          <p className={s.reportBlockValue}>{checkinData.technicalIssueDetails}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Not submitted — check-in form */
                    <div>
                      <p className={s.formIntro}>How did this week go for your group?</p>

                      {/* Engagement Level */}
                      <div className={s.formGroup}>
                        <label className={s.formLabel}>Engagement Level *</label>
                        <div className={s.engOptions}>
                          {['low','medium','high'].map(level => (
                            <button key={level} className={`${s.engBtn} ${engLevel === level ? s.engBtnSelected : ''}`}
                              onClick={() => setEngLevel(level)}>
                              {level.charAt(0).toUpperCase() + level.slice(1)}
                            </button>
                          ))}
                        </div>
                        <span className={s.formHint}>Your read on how engaged participants were with prompts this week</span>
                      </div>

                      {/* Concerns */}
                      <div className={s.formGroup}>
                        <label className={s.formLabel}>Issues & Concerns</label>
                        <textarea className={s.formTextarea} value={concerns} onChange={e => setConcerns(e.target.value)}
                          placeholder="Any participant or technical concerns this week?" rows={3} />
                      </div>

                      {/* Verbatim */}
                      <div className={s.formGroup}>
                        <label className={s.formLabel}>Participant Verbatim</label>
                        <textarea className={s.formTextarea} value={verbatim} onChange={e => setVerbatim(e.target.value)}
                          placeholder="A quote or moment that stood out this week" rows={2} />
                        <span className={s.formHint}>Participant words that stood out to you</span>
                      </div>

                      {/* Positive Feedback */}
                      <div className={s.formGroup}>
                        <label className={s.formLabel}>Positive Feedback</label>
                        <textarea className={s.formTextarea} value={positiveFeedback} onChange={e => setPositiveFeedback(e.target.value)}
                          placeholder="What went well this week?" rows={2} />
                      </div>

                      {/* Technical Issues */}
                      <div className={s.formGroup}>
                        <label className={s.formLabel}>Technical Issues?</label>
                        <div className={s.toggleRow}>
                          <button className={`${s.togBtn} ${techIssues === true ? s.togBtnYes : ''}`} onClick={() => setTechIssues(true)}>Yes</button>
                          <button className={`${s.togBtn} ${techIssues === false ? s.togBtnNo : ''}`} onClick={() => setTechIssues(false)}>No</button>
                        </div>
                        {techIssues === true && (
                          <textarea className={s.formTextarea} value={techDetails} onChange={e => setTechDetails(e.target.value)}
                            placeholder="Describe the technical issues" rows={2} style={{ marginTop:8 }} />
                        )}
                      </div>

                      {/* Follow-up Needed */}
                      <div className={s.formGroup}>
                        <label className={s.formLabel}>Follow-up Needed?</label>
                        <div className={s.toggleRow}>
                          <button className={`${s.togBtn} ${followUp === true ? s.togBtnYes : ''}`} onClick={() => setFollowUp(true)}>Yes</button>
                          <button className={`${s.togBtn} ${followUp === false ? s.togBtnNo : ''}`} onClick={() => setFollowUp(false)}>No</button>
                        </div>
                        {followUp === true && (
                          <textarea className={s.formTextarea} value={followUpNotes} onChange={e => setFollowUpNotes(e.target.value)}
                            placeholder="What follow-up is needed?" rows={2} style={{ marginTop:8 }} />
                        )}
                      </div>

                      {/* Submit */}
                      <button className={s.btnSubmit} onClick={handleSubmitCheckin} disabled={submitting || !engLevel}>
                        {submitting ? 'Submitting...' : 'Submit Check-In'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* Future week */}
        {backSpaceId && selectedWeek > 0 && isFutureWeek && (
          <div className={s.futureMsg}>
            <div className={s.futureIcon}>🗓️</div>
            <p>Week {selectedWeek} hasn't started yet.</p>
            <p style={{ fontSize:'var(--ds)', marginTop:4 }}>Check back when this week begins.</p>
          </div>
        )}

        {/* ══════════════════════════════════════════
            FOOTER
            ══════════════════════════════════════════ */}
        <div className={s.footer}>
          <p>Questions or need help? Reach out anytime.</p>
          <a href="mailto:support@anamoria.com" className={s.supportLink}>support@anamoria.com</a>
        </div>

      </div>

      {/* ══════════════════════════════════════════
          QR MODAL
          ══════════════════════════════════════════ */}
      {showQrModal && (
        <div className={s.modalOverlay} onClick={() => setShowQrModal(false)}>
          <div className={s.modalBox} onClick={e => e.stopPropagation()}>
            <div className={s.modalHead}>
              <h3 className={s.modalTitle}>Group QR Code</h3>
              <button className={s.btnClose} onClick={() => setShowQrModal(false)}>✕</button>
            </div>
            <div className={s.modalBody}>
              <QRCodeSVG value={joinUrl} size={240} level="M" />
              <p className={s.modalHint}>Participants scan this to join your group</p>
            </div>
          </div>
        </div>
      )}

      {/* GS-11 — Bulk allowlist upload (paste/CSV) */}
      {showEmailUpload && (
        <EmailListUploadModal
          getApi={getApi}
          groupParam={groupParam}
          onClose={() => setShowEmailUpload(false)}
          onAdded={() => { if (!checklist.participants) toggleCheck('participants'); }}
        />
      )}

      {/* v1.6: create a group space (Group preselected) via the shared modal */}
      {showCreateSpace && (
        <CreateSpaceModal
          getApi={getApi}
          groupParam={groupParam}
          initialSpaceType={createSpaceIntent === 'personal' ? 'private' : 'group'}
          onCreated={(newSpaceId) => {
            // 2c: creating this cohort's group space — reload the dashboard for the selected cohort
            // (groupSpaceId populates, the empty state clears, the back-link appears). Otherwise (B3
            // invite flow) go straight to the new space's bulk-invite page, unchanged.
            if (createSpaceIntent === 'personal') { return; } // stay on dashboard; phase flips as appState.spaces updates
            if (createSpaceIntent === 'cohort') { setDashReloadKey(k => k + 1); return; }
            navigate(`/spaces/${newSpaceId}/invite/bulk`);
          }}
          onClose={() => setShowCreateSpace(false)}
        />
      )}

      {/* ══════════════════════════════════════════
          B3 — INVITE SPACE PICKER MODAL
          ══════════════════════════════════════════ */}
      {showInvitePicker && (
        <div className={s.modalOverlay} onClick={() => setShowInvitePicker(false)}>
          <div className={s.modalBox} onClick={e => e.stopPropagation()}>
            <div className={s.modalHead}>
              <h3 className={s.modalTitle}>Which space would you like to invite people to?</h3>
              <button className={s.btnClose} onClick={() => setShowInvitePicker(false)}>✕</button>
            </div>
            <div className={s.modalBody}>
              {(() => {
                // v0.3: only GROUP spaces can receive leader-dashboard invites.
                const groupSpaces = (appState?.spaces || []).filter(sp => sp.isGroupSpace === true);
                return (
                  <>
                    {groupSpaces.length === 0 && (
                      <p className={s.pickerEmpty}>You don&apos;t have any group spaces yet. Create a group space to invite people to contribute.</p>
                    )}
                    {groupSpaces.map(sp => (
                      <button
                        key={sp.id}
                        className={s.pickerRow}
                        onClick={() => navigate(`/spaces/${sp.id}/invite/bulk`)}
                      >
                        {sp.name}
                      </button>
                    ))}
                    {/* v1.6: always offer creating a new group space (Group preselected). */}
                    <button
                      className={s.pickerCreateRow}
                      onClick={() => { setShowInvitePicker(false); setCreateSpaceIntent('invite'); setShowCreateSpace(true); }}
                    >
                      + New group space
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════
          CONFIRM CHECK-IN MODAL
          ══════════════════════════════════════════ */}
      {showConfirmModal && (
        <div className={s.modalOverlay} onClick={() => setShowConfirmModal(false)}>
          <div className={s.modalBox} onClick={e => e.stopPropagation()}>
            <div className={s.modalHead}>
              <h3 className={s.modalTitle}>Submit Check-In</h3>
              <button className={s.btnClose} onClick={() => setShowConfirmModal(false)}>✕</button>
            </div>
            <div className={s.confirmBody}>
              <p className={s.confirmText}>
                Check-ins are locked after submission and cannot be edited. Are you sure you want to submit?
              </p>
              <div className={s.confirmActions}>
                <button className={s.btnCancel} onClick={() => setShowConfirmModal(false)}>Cancel</button>
                <button className={s.btnConfirm} onClick={confirmSubmitCheckin} disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && <div className={s.toast}>✓ {toast}</div>}
    </div>
  );
}
